import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

export interface TencentAdpCredential {
  created_at?: string | null
  id: string
  masked_tencent_secret_id: string
  masked_tencent_secret_key: string
  name: string
  openapi_endpoint: string
  tencent_region: string
  tencent_secret_id: string
  updated_at?: string | null
}

export type TencentAdpCredentialOption = TencentAdpCredential

export interface TencentAdpCredentialCreatePayload {
  name: string
  openapi_endpoint: string
  tencent_region: string
  tencent_secret_id: string
  tencent_secret_key: string
}

export interface TencentAdpCredentialUpdatePayload {
  name?: string
  openapi_endpoint?: string
  tencent_region?: string
  tencent_secret_id?: string
  tencent_secret_key?: string
}

export interface TencentAdpValidationStage {
  elapsed_ms: number
  key: string
  message: string
  status: string
}

export interface TencentAdpValidationResult {
  app_id?: string | null
  app_name?: string | null
  app_status?: string | null
  message: string
  ok: boolean
  request_id?: string | null
  stages: TencentAdpValidationStage[]
}

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

interface MessageEnvelope {
  code: 'success'
  message: string
}

export const tencentAdpCredentialsQueryKey = ['admin-adp-credentials'] as const
export const tencentAdpCredentialOptionsQueryKey = ['admin-app-adp-credentials'] as const

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('admin'),
  portal: 'admin',
})

function trimmedOrUndefined(value: string | undefined) {
  const normalized = value?.trim()

  if (!normalized) {
    return undefined
  }

  return normalized
}

function createPayload(values: TencentAdpCredentialCreatePayload) {
  return {
    name: values.name.trim(),
    openapi_endpoint: values.openapi_endpoint.trim(),
    tencent_region: values.tencent_region.trim(),
    tencent_secret_id: values.tencent_secret_id.trim(),
    tencent_secret_key: values.tencent_secret_key.trim(),
  }
}

function updatePayload(values: TencentAdpCredentialUpdatePayload) {
  const payload: Record<string, string> = {}
  const secretId = trimmedOrUndefined(values.tencent_secret_id)
  const secretKey = trimmedOrUndefined(values.tencent_secret_key)

  if (values.name !== undefined) {
    payload.name = values.name.trim()
  }
  if (values.openapi_endpoint !== undefined) {
    payload.openapi_endpoint = values.openapi_endpoint.trim()
  }
  if (values.tencent_region !== undefined) {
    payload.tencent_region = values.tencent_region.trim()
  }
  if (secretId) {
    payload.tencent_secret_id = secretId
  }
  if (secretKey) {
    payload.tencent_secret_key = secretKey
  }

  return {
    ...payload,
  }
}

export async function listTencentAdpCredentials() {
  const response =
    await apiClient.request<SuccessEnvelope<{ items: TencentAdpCredential[] }>>(
      '/admin/adp-credentials',
    )

  return response.data.items
}

export async function listTencentAdpCredentialOptions() {
  return listTencentAdpCredentials()
}

export async function createTencentAdpCredential(values: TencentAdpCredentialCreatePayload) {
  const response = await apiClient.request<SuccessEnvelope<TencentAdpCredential>>(
    '/admin/adp-credentials',
    {
      body: JSON.stringify(createPayload(values)),
      method: 'POST',
    },
  )

  return response.data
}

export async function validateTencentAdpCredentialDraft(values: TencentAdpCredentialCreatePayload) {
  const response = await apiClient.request<SuccessEnvelope<TencentAdpValidationResult>>(
    '/admin/adp-credentials/validate',
    {
      body: JSON.stringify(createPayload(values)),
      method: 'POST',
    },
  )

  return response.data
}

export async function updateTencentAdpCredential({
  credentialId,
  values,
}: {
  credentialId: string
  values: TencentAdpCredentialUpdatePayload
}) {
  const response = await apiClient.request<SuccessEnvelope<TencentAdpCredential>>(
    `/admin/adp-credentials/${encodeURIComponent(credentialId)}`,
    {
      body: JSON.stringify(updatePayload(values)),
      method: 'PUT',
    },
  )

  return response.data
}

export async function validateTencentAdpCredentialUpdate({
  credentialId,
  values,
}: {
  credentialId: string
  values: TencentAdpCredentialUpdatePayload
}) {
  const response = await apiClient.request<SuccessEnvelope<TencentAdpValidationResult>>(
    `/admin/adp-credentials/${encodeURIComponent(credentialId)}/validate`,
    {
      body: JSON.stringify(updatePayload(values)),
      method: 'POST',
    },
  )

  return response.data
}

export async function deleteTencentAdpCredential(credentialId: string) {
  await apiClient.request<MessageEnvelope>(
    `/admin/adp-credentials/${encodeURIComponent(credentialId)}`,
    {
      method: 'DELETE',
    },
  )
}
